import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { EventsConfig } from 'src/app/instant-analysis/models/events-config';
import embed, { Mode, VisualizationSpec } from 'vega-embed';
import { SearchFieldComponent } from '../search-field/search-field.component';
import { EventGroups } from '../../instant-analysis/models/event-groups';

/**
 * Gráfico de barras das ocorrências registradas dentro do intervalo de tempo
 * selecionado pelo usuário.
 */
@Component({
  selector: 'app-event-chart',
  templateUrl: './event-chart.component.html',
  styleUrls: ['./event-chart.component.css']
})
export class EventChartComponent implements OnInit {

  startDate: string;
  endDate: string;
  events: any[];
  eventCount: {cause: string, count: number, neighborhood: string}[];
  selectedNeighborhoods: string[] = [];
  isExpansionPanelOpen: boolean = false;
  /**
  * Textos do tooltips do eventchartbar.
  */
  eventChartTooltipText:string = `Gráfico de ocorrências. Mostra o total de 
    ocorrências na cidade. Permite filtrar as ocorrências por bairro.`;

  @Output() dragDisabled = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<boolean>();

  @ViewChild('searchField') searchField: SearchFieldComponent;

  constructor() { }

  ngOnInit(): void {
  }

  /**
   * 
   * @param event 
   */
  headerMouseDown(event: any) {
    this.dragDisabled.emit(false)
  }
  
  /**
   * 
   * @param event 
   */
  headerMouseUp(event: any) {
    this.dragDisabled.emit(true);
  }

  /**
   * Sinaliza o fechamento do componente do gráfico
   */
  closeChart() {
    this.closeExpansionPanel();
    this.close.emit();
  }

  /** 
   * Fecha o expansion panel do accordion. Isso é importante
   * para evitar que o expansion panel fique "por cima" do mapa
   * quando o componente do gráfico for fechado com visibility hidden.
   */
  closeExpansionPanel() {
    this.isExpansionPanelOpen = false;
  }
  

  /**
   * Inicializa os atributos do gráfico.
   * @param eventsConfig configuração do gráfico, contendo a FeatureCollection das ocorrências, 
   *    a data inicial e a data final 
   */
  initialize(eventsConfig: EventsConfig) {

    this.startDate = eventsConfig.startDate;
    this.endDate = eventsConfig.endDate;

    this.events = eventsConfig.events.features.map(feature => feature.properties);
    this.initializeSearchField(this.events);
  }

  /**
   * Inicializa o campo de busca de bairros.
   * @param events registros das ocorrências, incluindo o nome do bairro da ocorrência.
   */
  initializeSearchField(events: { [name: string]: any; }[]) {
    const neighborhoods = new Set(events.map(ev => ev.bairro));
    this.searchField.allOptions = Array.from(neighborhoods);
  }

  /**
   * Obtém a contagem de ocorrências separadas por bairro e pela cidade inteira.
   * @returns array com registros de motivo, contagem e bairro das ocorrências.
   */
  private getEventCount(): {cause: string, count: number, neighborhood: string}[] {

    const cityEventsCount = this.calcGlobalEventCount(this.events);
    const filteredEvents = this.events.filter(ev => this.selectedNeighborhoods.includes(ev.bairro));
    const selectedNeighborhoodEventsCount = this.calcNeighborhoodEventCount(filteredEvents);
    return[...cityEventsCount, ...selectedNeighborhoodEventsCount];
  }

  /**
   * Inicializa a contagem de eventos para a cidade como um todo.
   * @param events 
   */
  calcGlobalEventCount(events: {[key: string]: any}[]) {

    let eventCount: {[key: string]: {count: number, cause: string, neighborhood: string}} = {};
    for (let { motivo } of events) {
      motivo = EventGroups.getGroup(motivo);
      if (motivo in eventCount) {
        eventCount[motivo]['count']++;
      }
      else {
        eventCount[motivo] = {'count': 1, 'cause': motivo, 'neighborhood': 'CIDADE DE NITERÓI'};
      }
    }
    return Object.values(eventCount);
  }

  /**
   * Inicializa a contagem de cada tipo de ocorrência, que será usada para construir o gráfico de barras.
   * @param events array das properties de cada feature na FeatureCollection. 
   *    Cada item do array contém a chave 'motivo'.
   */
  calcNeighborhoodEventCount(events: {[key: string]: any}[]) {
    let eventCount: {[key: string]: any} = {};
    for (let {bairro, motivo} of events) {
      motivo = EventGroups.getGroup(motivo);
      if (bairro in eventCount) {
        if (motivo in eventCount[bairro]) {
          eventCount[bairro][motivo]['count']++;
        }
        else {
          eventCount[bairro][motivo] = {'count': 1, 'cause': motivo};
        }
      }
      else {
        eventCount[bairro] = {}
        eventCount[bairro][motivo] = {'count': 1, 'cause': motivo};
      }
    }

    return this.unflattenNeighborhoodEventCount(eventCount);
  }

  /**
   * Transforma um objeto cujas chaves externas são nomes de bairros, com valores sendo objetos
   * cujas chaves são nomes de ocorrências, cujos valores são a contagem e o nome da ocorrência,
   * em um array no qual cada item tem o nome do bairro, o nome da ocorrência e a contagem da 
   * ocorrência no bairro.
   * 
   * @param eventCount 
   * @returns dados no formato tabular para o vega-lite
   */
  private unflattenNeighborhoodEventCount(eventCount: { [key: string]: any; }) {
    let result = [];
    for (let neighborhood of Object.keys(eventCount)) {
      for (let { count, cause } of Object.values(eventCount[neighborhood]) as { [key: string]: any; }[]) {
        result.push({ count, cause, neighborhood });
      }
    }
    return result;
  }

  /**
   * Atualiza o gráfico de barras.
   */
  async updateChart() {

    let spec : VisualizationSpec = this.createChartSpec();

    const embedOpts = {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      },
      mode: 'vega-lite' as Mode,
      defaultStyle: true,
      i18n: {
        PNG_ACTION: "Salvar como PNG",
        SVG_ACTION: "Salvar como SVG",
      }
    };

    await embed('#event-chart-area', spec, embedOpts);
  }

  /**
   * Cria a especificação do gráfico de barras horizontais do vega-lite.
   * @returns especificação do gráfico
   */
  createChartSpec() {

    const eventCount = this.getEventCount();
    const eventCategories = Array.from(new Set(eventCount.map(e => e.cause)));
    const eventColors = eventCategories.map(cat => EventGroups.getColor(cat));

    const spec: VisualizationSpec = {
      'width': 'container',
      'data': {
        'values': eventCount
      },
      'params': [
        {
          'name': 'grid',
          'select': {'type': 'interval', 'encodings': ['x']},
          'bind': 'scales',
        }
      ],
      'encoding': {
        'x': {'field': 'count', 'type': 'quantitative', 'title': 'Contagem'},
        'y': {'field': 'neighborhood', 'type': 'nominal', 'title': 'Bairro'},
        'color': {'field': 'cause', 'type': 'nominal', 'title': 'Motivo',
          'scale': {
            'domain': eventCategories,
            'range': eventColors,
          }
        },
        'tooltip': [
          {'field': 'cause', 'type': 'nominal', 'title': 'Motivo'},
          {'field': 'count', 'type': 'quantitative', 'title': 'Contagem'}
        ]
      },
      'layer': [
        {'mark': 'bar'},
      ],
    };

    return spec;
  }

  /**
   * Obtém a string do intervalo de datas para o título do componente
   * @returns intervalo de datas, se estas forem definidas, ou string vazia, caso contrário.
   */
  getDateInterval(): string {
    return this.startDate && this.endDate ? `de ${this.startDate} a ${this.endDate}` : '';
  }

  /**
   * Reage a mudanças no filtro de bairros, solicitando a atualização do gráfico.
   */
  async onSelectedOptionsChanged(options: string[]) {
    this.selectedNeighborhoods = options;
    await this.updateChart();
  }

}
